import { useState } from 'react';
import {
    AVAILABILITY_OPTIONS,
    GENDER_OPTIONS,
    STATUS_OPTIONS,
    META_TITLE_MAX,
    META_DESC_MAX,
    KEYWORDS_MAX,
    slugify,
    generateSku,
    autoMetaTitle,
    autoMetaDescription,
    autoKeywords,
    autoCanonicalUrl,
    countCharsClass,
    SITE_ORIGIN,
} from '../Funtions/seo';
import './seo_panel.css';

/**
 * SEO Panel — implements admin-panel-seo-spec.md sections 2.1–2.9 (UI bits)
 * Pure controlled component: all state lives in parent.
 */
export default function SeoPanel({
    // identity: `ctx` carries name, kind, category and discovery tags for auto-fill
    ctx, name, category, skuSeed,
    // controlled values
    slug, setSlug,
    sku, setSku,
    subCategory, setSubCategory,
    metaTitle, setMetaTitle,
    metaDescription, setMetaDescription,
    keywords, setKeywords,
    canonicalUrl, setCanonicalUrl,
    noindex, setNoindex,
    ogImage, setOgImage,
    longDescription, setLongDescription,
    faqs, setFaqs,
    material, setMaterial,
    pattern, setPattern,
    colorName, setColorName,
    colors, setColors,
    gender, setGender,
    availability, setAvailability,
    status, setStatus,
    redirectSlugs, setRedirectSlugs,
    videoUrl, setVideoUrl,
    docId,
}) {
    const [keywordInput, setKeywordInput] = useState('');
    const [colorInput, setColorInput] = useState('#000000');

    const previewCanonical = canonicalUrl
        || autoCanonicalUrl({ category, slug: slug || slugify(name), docId: docId || '{id}' });

    const previewTitle = metaTitle || autoMetaTitle(ctx) || name || 'Product';
    const previewDesc = metaDescription || autoMetaDescription(ctx);

    const regenerateSlug = () => setSlug(slugify(name));
    const regenerateSku = () => setSku(generateSku(skuSeed || category));
    const suggestTitle = () => setMetaTitle(autoMetaTitle(ctx));
    const suggestDesc = () => setMetaDescription(autoMetaDescription(ctx));
    const suggestKw = () => setKeywords(autoKeywords(ctx));

    const addKeyword = (raw) => {
        const v = String(raw || '').trim().toLowerCase();
        if (!v) return;
        if (keywords.includes(v) || keywords.length >= KEYWORDS_MAX) return;
        setKeywords([...keywords, v]);
        setKeywordInput('');
    };
    const removeKeyword = (i) => setKeywords(keywords.filter((_, idx) => idx !== i));

    const addColor = () => {
        const v = (colorInput || '').toLowerCase();
        if (!v || colors.includes(v)) return;
        setColors([...colors, v]);
    };
    const removeColor = (i) => setColors(colors.filter((_, idx) => idx !== i));

    const addFaq = () => setFaqs([...faqs, { q: '', a: '' }]);
    const updateFaq = (i, key, val) => {
        const next = [...faqs];
        next[i] = { ...next[i], [key]: val };
        setFaqs(next);
    };
    const removeFaq = (i) => setFaqs(faqs.filter((_, idx) => idx !== i));

    const titleCls = countCharsClass(previewTitle, META_TITLE_MAX);
    const descCls = countCharsClass(previewDesc, META_DESC_MAX);

    return (
        <div className="seo-panel">
            {/* SERP preview */}
            <div>
                <div className="seo-serp-label">Google Search Preview</div>
                <div className="seo-serp">
                    <div className="seo-serp-title">
                        {previewTitle.length > META_TITLE_MAX
                            ? previewTitle.slice(0, META_TITLE_MAX - 1) + '…'
                            : previewTitle}
                    </div>
                    <div className="seo-serp-url">{previewCanonical || `${SITE_ORIGIN}/products/...`}</div>
                    <div className="seo-serp-desc">
                        {previewDesc
                            ? (previewDesc.length > META_DESC_MAX
                                ? previewDesc.slice(0, META_DESC_MAX - 1) + '…'
                                : previewDesc)
                            : <em>Description will auto-build from product details if left blank.</em>}
                    </div>
                </div>
            </div>

            {/* Identity */}
            <div className="seo-grid-2">
                <div className="f-inputs">
                    <label className="button-upload-label">Slug</label>
                    <div className="seo-row">
                        <input
                            type="text"
                            value={slug}
                            onChange={(e) => setSlug(slugify(e.target.value))}
                            placeholder="auto-generated-from-name"
                        />
                        <button type="button" className="seo-btn seo-btn-ghost" onClick={regenerateSlug}>Regenerate</button>
                    </div>
                    <div className="seo-hint">URL: {previewCanonical || '—'}</div>
                </div>

                <div className="f-inputs">
                    <label className="button-upload-label">SKU</label>
                    <div className="seo-row">
                        <input
                            type="text"
                            value={sku}
                            onChange={(e) => setSku(e.target.value)}
                            placeholder={`MAV-${String(skuSeed || category || 'PRD').replace(/_/g, '').toUpperCase().slice(0, 6)}-0142 (auto if blank)`}
                        />
                        <button type="button" className="seo-btn seo-btn-ghost" onClick={regenerateSku}>Auto</button>
                    </div>
                </div>

                <div className="f-inputs">
                    <label className="button-upload-label">Sub-Category</label>
                    <input type="text" value={subCategory} onChange={(e) => setSubCategory(e.target.value)} placeholder="bandhgala, tuxedo, sherwani…" />
                </div>

                <div className="f-inputs">
                    <label className="button-upload-label">Status</label>
                    <div className="seo-status-row">
                        <select value={status} onChange={(e) => setStatus(e.target.value)}>
                            {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                        <span className={`seo-status-pill ${status}`}>{status}</span>
                    </div>
                </div>
            </div>

            {/* Meta tags */}
            <div className="f-inputs">
                <label className="button-upload-label">Meta Title</label>
                <div className="seo-row">
                    <input
                        type="text"
                        value={metaTitle}
                        onChange={(e) => setMetaTitle(e.target.value)}
                        placeholder="Leave blank to auto-build"
                        maxLength={120}
                    />
                    <button type="button" className="seo-btn seo-btn-ghost" onClick={suggestTitle}>Suggest</button>
                </div>
                <div className="seo-counter">
                    <span className="seo-hint">Aim for ≤ {META_TITLE_MAX} chars</span>
                    <span className={titleCls}>{(metaTitle || '').length} / {META_TITLE_MAX}</span>
                </div>
            </div>

            <div className="f-inputs">
                <label className="button-upload-label">Meta Description</label>
                <div className="seo-row">
                    <textarea
                        rows={3}
                        value={metaDescription}
                        onChange={(e) => setMetaDescription(e.target.value)}
                        placeholder="Leave blank to auto-build from description, material & occasion"
                        maxLength={300}
                    />
                </div>
                <div className="seo-row" style={{ justifyContent: 'flex-end' }}>
                    <button type="button" className="seo-btn seo-btn-ghost" onClick={suggestDesc}>Suggest</button>
                </div>
                <div className="seo-counter">
                    <span className="seo-hint">Aim for ≤ {META_DESC_MAX} chars</span>
                    <span className={descCls}>{(metaDescription || '').length} / {META_DESC_MAX}</span>
                </div>
            </div>

            <div className="f-inputs">
                <label className="button-upload-label">Keywords ({keywords.length}/{KEYWORDS_MAX})</label>
                <div className="seo-tags">
                    {keywords.map((k, i) => (
                        <span className="seo-tag" key={`${k}-${i}`}>
                            {k}
                            <button type="button" onClick={() => removeKeyword(i)} aria-label="Remove">×</button>
                        </span>
                    ))}
                    <input
                        className="seo-tag-input"
                        value={keywordInput}
                        onChange={(e) => setKeywordInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ',') {
                                e.preventDefault();
                                addKeyword(keywordInput);
                            } else if (e.key === 'Backspace' && !keywordInput && keywords.length) {
                                removeKeyword(keywords.length - 1);
                            }
                        }}
                        placeholder={keywords.length < KEYWORDS_MAX ? 'Type and press Enter' : 'Max reached'}
                        disabled={keywords.length >= KEYWORDS_MAX}
                    />
                </div>
                <div className="seo-row" style={{ justifyContent: 'flex-end' }}>
                    <button type="button" className="seo-btn seo-btn-ghost" onClick={suggestKw}>Suggest from product</button>
                </div>
            </div>

            <div className="seo-grid-2">
                <div className="f-inputs">
                    <label className="button-upload-label">Canonical URL (optional)</label>
                    <input
                        type="url"
                        value={canonicalUrl}
                        onChange={(e) => setCanonicalUrl(e.target.value)}
                        placeholder="leave blank to auto-build"
                    />
                </div>
                <div className="f-inputs">
                    <label className="button-upload-label">OG Image URL (optional)</label>
                    <input
                        type="url"
                        value={ogImage}
                        onChange={(e) => setOgImage(e.target.value)}
                        placeholder="Defaults to first product image"
                    />
                </div>
            </div>

            <div className="seo-row">
                <label className="seo-toggle">
                    <input type="checkbox" checked={!!noindex} onChange={(e) => setNoindex(e.target.checked)} />
                    <span>noindex (hide from Google — drafts/internal)</span>
                </label>
            </div>

            {/* Long description */}
            <div className="f-inputs">
                <label className="button-upload-label">Long Description (SEO body copy)</label>
                <textarea
                    rows={6}
                    value={longDescription}
                    onChange={(e) => setLongDescription(e.target.value)}
                    placeholder="500–1500 chars. Fabric story, occasion notes, styling tips. Use markdown for H2/H3."
                />
                <div className="seo-counter">
                    <span className="seo-hint">Recommended 500–1500 chars</span>
                    <span className={(longDescription || '').length < 300 ? 'warn' : 'ok'}>
                        {(longDescription || '').length} chars
                    </span>
                </div>
            </div>

            {/* Attributes */}
            <div className="seo-grid-2">
                <div className="f-inputs">
                    <label className="button-upload-label">Material</label>
                    <input type="text" value={material} onChange={(e) => setMaterial(e.target.value)} placeholder="Italian wool 220 GSM" />
                </div>
                <div className="f-inputs">
                    <label className="button-upload-label">Pattern</label>
                    <input type="text" value={pattern} onChange={(e) => setPattern(e.target.value)} placeholder="Solid, Pin-stripe, Checks…" />
                </div>
                <div className="f-inputs">
                    <label className="button-upload-label">Color Name (display)</label>
                    <input type="text" value={colorName} onChange={(e) => setColorName(e.target.value)} placeholder="Midnight Blue" />
                    <div className="seo-hint">Also sets the colour family in Discovery unless you picked one manually.</div>
                </div>
                <div className="f-inputs">
                    <label className="button-upload-label">Gender</label>
                    <select value={gender} onChange={(e) => setGender(e.target.value)}>
                        {GENDER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </div>
                <div className="f-inputs">
                    <label className="button-upload-label">Availability</label>
                    <select value={availability} onChange={(e) => setAvailability(e.target.value)}>
                        {AVAILABILITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </div>
                <div className="f-inputs">
                    <label className="button-upload-label">Video URL (optional)</label>
                    <input type="url" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} placeholder="MP4 or YouTube embed URL" />
                </div>
            </div>

            <div className="f-inputs">
                <label className="button-upload-label">Color Swatches (hex)</label>
                <div className="seo-row">
                    <input
                        type="color"
                        value={colorInput}
                        onChange={(e) => setColorInput(e.target.value)}
                        style={{ width: 50, padding: 2, height: 38 }}
                    />
                    <input
                        type="text"
                        value={colorInput}
                        onChange={(e) => setColorInput(e.target.value)}
                        placeholder="#0d1330"
                        style={{ maxWidth: 140 }}
                    />
                    <button type="button" className="seo-btn" onClick={addColor}>Add color</button>
                </div>
                <div className="seo-color-list">
                    {colors.length === 0 && <span className="seo-hint">No colors added.</span>}
                    {colors.map((c, i) => (
                        <span className="seo-color-chip" key={`${c}-${i}`}>
                            <span className="seo-color-swatch" style={{ background: c }} />
                            {c}
                            <button type="button" onClick={() => removeColor(i)}>×</button>
                        </span>
                    ))}
                </div>
            </div>

            {/* FAQs */}
            <div className="f-inputs">
                <label className="button-upload-label">FAQs ({faqs.length}) — power FAQPage rich snippet</label>
                <div className="seo-faq-list">
                    {faqs.map((f, i) => (
                        <div className="seo-faq-item" key={i}>
                            <div className="seo-faq-head">
                                <span>FAQ #{i + 1}</span>
                                <button type="button" className="seo-btn seo-btn-danger" onClick={() => removeFaq(i)}>Remove</button>
                            </div>
                            <input
                                type="text"
                                value={f.q || ''}
                                onChange={(e) => updateFaq(i, 'q', e.target.value)}
                                placeholder="Question"
                            />
                            <textarea
                                rows={2}
                                value={f.a || ''}
                                onChange={(e) => updateFaq(i, 'a', e.target.value)}
                                placeholder="Answer"
                            />
                        </div>
                    ))}
                </div>
                <div className="seo-row" style={{ justifyContent: 'flex-start' }}>
                    <button type="button" className="seo-btn" onClick={addFaq}>+ Add FAQ</button>
                </div>
            </div>

            <div className="seo-hint">
                Star ratings appear on Google only from verified customer reviews collected after delivery — they cannot be typed in here.
            </div>

            {/* Redirect slugs */}
            <div className="f-inputs">
                <label className="button-upload-label">Redirect Slugs (old URLs → 301 to canonical)</label>
                <input
                    type="text"
                    value={(redirectSlugs || []).join(', ')}
                    onChange={(e) => setRedirectSlugs(
                        e.target.value.split(',').map((s) => slugify(s.trim())).filter(Boolean)
                    )}
                    placeholder="old-slug-1, old-slug-2"
                />
                <div className="seo-hint">Comma-separated. Auto-slugified.</div>
            </div>
        </div>
    );
}
